import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useSettingsStore, useUserStore } from '../store';
import { api } from '../services/api';
import { Region } from '../types';
import { CODE_SERVICES, codeServicePath } from './CodesPage';

interface RegionOption {
  code: Region;
  name: string;
  subtitle: string;
  image?: string;
  flagEmoji: string;
  gradient: string;
}

const REGIONS: RegionOption[] = [
  {
    code: 'TR',
    name: 'Турция',
    subtitle: 'Самый популярный',
    image: '/regions/tr.webp',
    flagEmoji: '🇹🇷',
    gradient: 'linear-gradient(135deg, #E30A17 0%, #8B0510 100%)',
  },
  {
    code: 'IN',
    name: 'Индия',
    subtitle: 'Новинки дешевле',
    image: '/regions/in.webp',
    flagEmoji: '🇮🇳',
    gradient: 'linear-gradient(135deg, #FF9933 0%, #138808 100%)',
  },
  {
    code: 'UA',
    name: 'Украина (СНГ)',
    subtitle: 'Больше игр на русском',
    image: '/regions/ua.webp',
    flagEmoji: '',
    gradient: 'linear-gradient(135deg, #5370F5 0%, #3657F0 100%)',
  },
];

const bonusWord = (n: number): string => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return 'бонусов';
  if (m10 === 1) return 'бонус';
  if (m10 >= 2 && m10 <= 4) return 'бонуса';
  return 'бонусов';
};

// Цвета сайта: главная mini app сверстана по макету «Стиль бота под сайт»
const ACCENT = '#0B63F6';
const INK = '#0A1F44';
const CARD_SHADOW = '0 1px 2px rgba(10,31,68,0.06), 0 12px 28px rgba(10,31,68,0.18)';
const PRESS: React.CSSProperties = { transition: 'transform 0.25s cubic-bezier(0.2,0.8,0.2,1)' };

/** Заголовок секции: значок, название и ссылка справа */
const SectionHead: React.FC<{ icon: React.ReactNode; top: number; action?: React.ReactNode; children: React.ReactNode }> = ({
  icon,
  top,
  action,
  children,
}) => (
  <div className="flex items-center" style={{ gap: 10, margin: `${top}px 0 14px` }}>
    {icon}
    <h3 className="flex-1" style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em', color: INK }}>
      {children}
    </h3>
    {action}
  </div>
);

/** Карточка раздела: текст слева, значок сервиса выглядывает из угла */
const ServiceCard: React.FC<{ title: string; subtitle: React.ReactNode; logo: string; rgb: string; deep: string; onClick: () => void }> = ({
  title,
  subtitle,
  logo,
  rgb,
  deep,
  onClick,
}) => (
  <button
    onClick={onClick}
    className="relative flex w-full flex-col overflow-hidden text-left active:scale-[0.98]"
    style={{
      ...PRESS,
      minHeight: 112,
      padding: 16,
      borderRadius: 20,
      border: '1px solid rgba(255,255,255,0.08)',
      background: `radial-gradient(ellipse 130% 120% at 90% 100%, rgba(${rgb},0.55), transparent 60%), radial-gradient(ellipse 60% 50% at 8% 0%, rgba(${rgb},0.18), transparent 55%), linear-gradient(135deg, #10151F 0%, ${deep} 100%)`,
      boxShadow: CARD_SHADOW,
    }}
  >
    <div className="relative" style={{ maxWidth: '60%' }}>
      <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: '-0.01em', color: '#FFFFFF' }}>{title}</div>
      <div style={{ marginTop: 4, fontSize: 13, fontWeight: 300, color: 'rgba(255,255,255,0.72)' }}>{subtitle}</div>
    </div>
    <div
      className="pointer-events-none absolute"
      style={{ right: -16, bottom: -24, transform: 'rotate(-14deg)', filter: 'drop-shadow(0 14px 26px rgba(0,0,0,0.45))' }}
    >
      <img src={logo} alt="" className="block" style={{ width: 108, height: 108, borderRadius: 22 }} />
    </div>
  </button>
);

/** «Позиции» (раздел шеринга) — по макету: фиолетовая карточка, плашка «Дешевле всего», стопка плиток со знаком PlayStation */
const PositionsCard: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={onClick}
    className="relative flex w-full flex-col overflow-hidden text-left active:scale-[0.98]"
    style={{
      ...PRESS,
      minHeight: 112,
      padding: 16,
      borderRadius: 20,
      border: '1px solid rgba(255,255,255,0.08)',
      background: 'radial-gradient(ellipse 130% 120% at 75% 100%, rgba(124,58,237,0.6), transparent 62%), radial-gradient(ellipse 60% 50% at 8% 0%, rgba(124,58,237,0.2), transparent 55%), linear-gradient(135deg, #140F26 0%, #2A1458 100%)',
      boxShadow: CARD_SHADOW,
    }}
  >
    <div className="relative" style={{ maxWidth: '60%' }}>
      <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: '-0.01em', color: '#FFFFFF' }}>Позиции</div>
      <div style={{ marginTop: 4, fontSize: 13, fontWeight: 400, lineHeight: 1.35, color: 'rgba(255,255,255,0.78)' }}>Готовые аккаунты с играми</div>
      <div className="flex items-center" style={{ marginTop: 10, gap: 5, fontSize: 11.5, fontWeight: 600, color: '#D9C8FF', whiteSpace: 'nowrap' }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#A98BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="block flex-none">
          <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" />
          <circle cx="7.5" cy="7.5" r="1.4" fill="#A98BFF" stroke="none" />
        </svg>
        Дешевле всего
      </div>
    </div>
    <div
      className="pointer-events-none absolute"
      style={{ right: -16, bottom: -24, transform: 'rotate(-14deg)', filter: 'drop-shadow(0 14px 26px rgba(0,0,0,0.45))' }}
    >
      <div
        className="absolute"
        style={{
          inset: 0,
          borderRadius: 22,
          background: 'linear-gradient(145deg, #8A5CF0 0%, #5A2CC4 60%, #3E1896 100%)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25), 0 6px 16px rgba(0,0,0,0.35)',
          transform: 'translate(-30px, -6px) rotate(-12deg)',
        }}
      />
      <div
        className="relative flex items-center justify-center"
        style={{
          width: 108,
          height: 108,
          borderRadius: 22,
          background: 'linear-gradient(145deg, #9B6BFF 0%, #6A35E0 55%, #4A1FB0 100%)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -10px 24px rgba(30,10,80,0.35), -4px -2px 12px rgba(20,5,60,0.35)',
        }}
      >
        <img src="/services/ps-mark-lavender.webp" alt="" className="block" style={{ width: 64, height: 64, objectFit: 'contain' }} />
      </div>
    </div>
  </button>
);

interface RegionSelectPageProps {
  /** Если true — показывает кнопку «Назад» вместо редиректа на home после выбора */
  editMode?: boolean;
}

export const RegionSelectPage: React.FC<RegionSelectPageProps> = ({ editMode = false }) => {
  const navigate = useNavigate();
  const setLastSelectedRegion = useUserStore((s) => s.setLastSelectedRegion);
  const currentRegion = useUserStore((s) => s.lastSelectedRegion);
  const { settings } = useSettingsStore();
  const cachedBonus = useUserStore((s) => s.cachedBonus);
  const setCachedBonus = useUserStore((s) => s.setCachedBonus);
  const [balance, setBalance] = useState<number | null>(cachedBonus);
  // Баланс мог доехать в хранилище уже после открытия экрана — подхватываем
  useEffect(() => {
    if (cachedBonus !== null) setBalance((prev) => (prev === null ? cachedBonus : prev));
  }, [cachedBonus]);
  const [codeKeys, setCodeKeys] = useState<Set<string> | null>(null); // null — витрина еще не ответила

  const referralEnabled = settings.referral?.enabled !== false;

  // Аватарка Telegram для хедера (photo_url бывает не у всех — фолбэк на иконку)
  const tgPhotoUrl = useMemo(() => {
    try {
      return ((window as any).Telegram?.WebApp?.initDataUnsafe?.user?.photo_url as string | undefined) || null;
    } catch {
      return null;
    }
  }, []);

  const isDesktop = useMemo(() => {
    try {
      const p = (window as any).Telegram?.WebApp?.platform as string | undefined;
      return p === 'tdesktop' || p === 'macos' || p === 'web' || p === 'weba' || p === 'webk';
    } catch {
      return false;
    }
  }, []);

  // Preload region images as soon as page opens
  useEffect(() => {
    REGIONS.forEach((r) => {
      if (r.image) {
        const img = new Image();
        img.src = r.image;
      }
    });
  }, []);

  useEffect(() => {
    api.getCodeServices()
      .then((r) => setCodeKeys(new Set(r.services.map((x) => x.key))))
      .catch(() => setCodeKeys(new Set()));
  }, []);

  // Первые четыре сервиса, что реально есть в наличии. Пока витрина
  // не ответила — первые из каталога, чтобы блок не прыгал.
  const codeTiles = useMemo(
    () => CODE_SERVICES.filter((x) => codeKeys === null || codeKeys.has(x.key)).slice(0, 4),
    [codeKeys]
  );

  // Баллы в хедере — тихо и без блокировки UI (в браузере без initData останется 0)
  useEffect(() => {
    if (!referralEnabled) return;
    api.getBalance()
      .then((b) => { setBalance(b.balance); setCachedBonus(b.balance); })
      .catch(() => {});
  }, [referralEnabled]);

  // PS Store: регион по умолчанию — Украина, сменить можно флажком в каталоге
  const handleOpenCatalog = () => {
    try {
      (window as any).Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('medium');
    } catch { /* ignore */ }
    if (!currentRegion) setLastSelectedRegion('UA');
    navigate('/home');
  };

  const handleSelect = (region: Region) => {
    try {
      (window as any).Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('medium');
    } catch { /* ignore */ }
    setLastSelectedRegion(region);
    if (editMode) {
      navigate(-1);
    } else {
      navigate('/home', { replace: true });
    }
  };

  const handleOpenChannel = () => {
    const url = 'https://telegram.me/+khTrheuMRIU5ZTFi';
    try {
      const tg = (window as any).Telegram?.WebApp;
      if (isDesktop) {
        if (tg?.openLink) {
          tg.openLink(url);
          return;
        }
      } else if (tg?.openTelegramLink) {
        tg.openTelegramLink(url);
        return;
      }
    } catch { /* ignore */ }
    window.open(url, '_blank', 'noopener');
  };

  return (
    <Layout showNav={false}>
    <div style={{ background: '#F5F7FA', minHeight: '100vh', lineHeight: 'normal' }}>
      <div className="mx-auto w-full max-w-[420px]" style={{ padding: '6px 16px 56px' }}>
        {/* Хедер: лого + бонусы + профиль */}
        <div className="flex items-center" style={{ gap: 10, padding: '10px 0 4px' }}>
          <img src="/xplay-logo.png" alt="XPlay" className="block flex-none" style={{ height: 22, width: 'auto' }} />
          {/* На самых узких экранах остается один значок: иначе бонусы не влезают */}
          <div className="whitespace-nowrap max-[359px]:hidden" style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.02em', color: INK }}>
            XPlay <span style={{ color: ACCENT }}>Store</span>
          </div>
          <div className="ml-auto flex flex-none items-center" style={{ gap: 10 }}>
            {referralEnabled && balance !== null && (
              <button
                onClick={() => navigate('/profile')}
                className="flex items-center whitespace-nowrap active:scale-[0.97]"
                style={{ ...PRESS, gap: 6, height: 38, padding: '0 12px', borderRadius: 13, background: '#FFFFFF', border: '1px solid #E6EAF2' }}
              >
                <svg width="17" height="19" viewBox="0 0 24 24" className="block">
                  <path
                    d="M12.5 2c.4 3-1.2 4.6-2.8 6.2C8 9.9 6.4 11.6 6.4 14.4a5.6 5.6 0 0 0 11.2 0c0-2.2-.9-3.9-2-5.3-.3 1-1 1.8-1.9 2 .6-2.9-.5-6.6-1.2-9.1z"
                    fill={ACCENT}
                  />
                  <path d="M12 13.2c1.4.9 2.2 2 2.2 3.2a2.2 2.2 0 0 1-4.4 0c0-1.2.8-2.3 2.2-3.2z" fill="#FFFFFF" fillOpacity="0.45" />
                </svg>
                <span style={{ fontSize: 14.5, fontWeight: 600, letterSpacing: '-0.01em', color: INK }}>
                  {balance.toLocaleString('ru-RU')} {bonusWord(balance)}
                </span>
              </button>
            )}
            <button
              onClick={() => navigate('/profile')}
              aria-label="Профиль"
              className="flex items-center justify-center overflow-hidden active:scale-[0.97]"
              style={{ ...PRESS, width: 38, height: 38, borderRadius: 13, background: '#FFFFFF', border: '1px solid #E6EAF2' }}
            >
              {tgPhotoUrl ? (
                <img src={tgPhotoUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="1.8" strokeLinecap="round">
                  <circle cx="12" cy="8" r="3.6" />
                  <path d="M4.5 20c1.4-3.4 4.2-5 7.5-5s6.1 1.6 7.5 5" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Каталог игр PlayStation — регион больше не спрашиваем на входе,
            он выбирается флажком в шапке каталога (по умолчанию Украина) */}
        {!editMode && (
          <>
            <SectionHead
              top={22}
              icon={<img src="/services/logo-psstore.webp" alt="" className="block flex-none" style={{ width: 26, height: 26, borderRadius: 8 }} />}
            >
              PlayStation
            </SectionHead>
            <div className="flex flex-col" style={{ gap: 12 }}>
              <ServiceCard
                title="PS Store"
                // Одной строкой: до значка ~100vw − 150px, строка ≈ 18,2 × кегль — кегль ужимается по ширине экрана (13px с ~400px,
                // не меньше 11px), чтобы до значка оставалось ~12px. Уже 360px — перенос; неразрывные пробелы держат «на ваш аккаунт» вместе
                subtitle={
                  <span className="whitespace-nowrap max-[359px]:whitespace-normal" style={{ fontSize: 'clamp(11px, calc((100vw - 162px) / 18.2), 13px)' }}>
                    {'Покупка игр и подписок на\u00A0ваш\u00A0аккаунт'}
                  </span>
                }
                logo="/services/logo-psstore.webp"
                rgb="11,99,246"
                deep="#0A2050"
                onClick={handleOpenCatalog}
              />
              {/* Позиции — раздел шеринга (игры и подписки на общих аккаунтах), как в макете — под PS Store */}
              <PositionsCard onClick={() => navigate('/sharing')} />
            </div>
          </>
        )}

        {/* Смена региона — экран /region-change из профиля */}
        {editMode && (
          <div className="flex flex-col gap-3" style={{ marginTop: 22 }}>
            <div>
              <h1
                className="font-bold tracking-[-0.4px]"
                style={{ fontFamily: 'Manrope, sans-serif', lineHeight: '120%', color: '#1A1D1A', fontSize: 'clamp(16px, 5.6vw, 21px)' }}
              >
                Выберите регион PlayStation
              </h1>
              <p className="text-sm text-text-secondary mt-1">Регион всегда можно сменить</p>
            </div>
            {REGIONS.map((r) => (
              <button
                key={r.code}
                onClick={() => handleSelect(r.code)}
                className="relative overflow-hidden rounded-2xl active:scale-[0.98] transition-transform w-full"
                style={{ background: r.gradient, aspectRatio: isDesktop ? '1300 / 370' : '1200 / 380' }}
              >
                {r.image && (
                  <img src={r.image} alt={r.name} className="absolute inset-0 w-full h-full object-cover" decoding="async" />
                )}
                <div className="absolute inset-0 flex items-center pl-[46%] pr-4">
                  <div className="flex-1 min-w-0 text-left">
                    <div
                      className="text-white text-[20px] font-bold text-left"
                      style={{ fontFamily: 'Manrope, sans-serif', lineHeight: '120%', textShadow: '0 2px 8px rgba(0,0,0,0.7)' }}
                    >
                      {r.name}
                    </div>
                    <div className="text-white/95 text-[13px] mt-0.5 text-left" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>
                      {r.subtitle}
                    </div>
                  </div>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 ml-3">
                    <path d="M9 6l6 6-6 6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Пополнение Steam */}
        {settings.steamTopupEnabled !== false && (
          <>
            <SectionHead
              top={26}
              icon={
                <img
                  src="/services/logo-steam.webp"
                  alt=""
                  className="block flex-none"
                  style={{ width: 26, height: 26, borderRadius: 8, boxShadow: '0 2px 6px rgba(10,31,68,0.18)' }}
                />
              }
            >
              Пополняйте Steam с нами
            </SectionHead>
            <ServiceCard
              title="Steam"
              subtitle="Пополнение баланса"
              logo="/services/logo-steam.webp"
              rgb="27,154,207"
              deep="#093142"
              onClick={() => navigate('/steam-topup')}
            />
          </>
        )}

        {/* Коды пополнения */}
        <SectionHead
          top={26}
          icon={
            <span className="flex flex-none items-center justify-center" style={{ width: 26, height: 26, borderRadius: 8, background: 'rgba(11,99,246,0.22)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round">
                <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" />
                <path d="M6.5 15h4M6.5 9.5h2" />
              </svg>
            </span>
          }
          action={
            <button
              onClick={() => navigate('/codes')}
              className="active:opacity-70 transition-opacity"
              style={{ fontSize: 13, fontWeight: 600, color: ACCENT }}
            >
              Смотреть все
            </button>
          }
        >
          Коды пополнения
        </SectionHead>
        <div className="grid grid-cols-4" style={{ gap: '14px 10px' }}>
          {codeTiles.map((s) => (
            <button
              key={s.key}
              onClick={() => navigate(codeServicePath(s.key))}
              className="flex flex-col active:scale-[0.95]"
              style={{ ...PRESS, gap: 7 }}
            >
              <img src={s.logo} alt={s.name} className="block w-full" style={{ aspectRatio: '1', objectFit: 'cover', borderRadius: 18 }} />
              <span className="w-full text-center" style={{ fontSize: 11.5, fontWeight: 500, lineHeight: 1.3, color: '#33415C' }}>
                {s.name}
              </span>
            </button>
          ))}
        </div>

        {/* Наш Telegram-канал */}
        <SectionHead
          top={26}
          icon={
            <span className="flex flex-none items-center justify-center" style={{ width: 26, height: 26, borderRadius: 8, background: 'rgba(34,158,217,0.18)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#229ED9">
                <path d="M21.9 3.6L2.9 10.9c-.95.37-.92 1.2.05 1.5l4.8 1.5 1.85 5.7c.28.82.93.94 1.5.42l2.63-2.53 4.9 3.6c.77.43 1.34.2 1.55-.72l3.1-14.9c.28-1.13-.42-1.65-1.38-1.27zM8.6 13.5l9.5-6-7.3 6.9-.3 3.1-1.9-4z" />
              </svg>
            </span>
          }
        >
          Наш Telegram-канал
        </SectionHead>
        <button
          onClick={handleOpenChannel}
          className="relative flex w-full items-center overflow-hidden text-left active:scale-[0.98]"
          style={{
            ...PRESS,
            gap: 14,
            padding: '22px 18px',
            borderRadius: 20,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'radial-gradient(ellipse 90% 140% at 0% 50%, rgba(11,99,246,0.35), transparent 62%), linear-gradient(135deg, #10151F 0%, #0B0F1A 100%)',
            boxShadow: CARD_SHADOW,
          }}
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute"
            style={{ left: -12, top: -34, fontSize: 130, fontWeight: 800, lineHeight: 1, letterSpacing: '-0.05em', color: 'rgba(11,99,246,0.28)' }}
          >
            %
          </span>
          <span className="relative flex-1" style={{ fontSize: 15.5, fontWeight: 700, lineHeight: 1.45, color: '#FFFFFF' }}>
            Новости, скидки, релизы, инструкции и розыгрыши
          </span>
          <span
            className="relative flex flex-none items-center justify-center"
            style={{
              width: 38,
              height: 38,
              borderRadius: 12,
              background: 'rgba(255,255,255,0.10)',
              border: '1px solid rgba(255,255,255,0.2)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 18 18 6M9 6h9v9" />
            </svg>
          </span>
        </button>
      </div>
    </div>
    </Layout>
  );
};
